import {
  ncrAffectsInventory,
  ncrCauseCategoryLabel,
  ncrDefectCategoryLabel,
  ncrDispositionLabel,
  ncrSourceLabel,
  ncrStatusLabel,
  NonconformanceReport,
} from '../../../types/nonconformanceReport';
import ApprovalStampBox from '../ApprovalStampBox';
import '../../contract/ContractPanel.css';
import '../../product/InspectionFormSheet.css';
import '../../product/NonconformanceReportPanel.css';

interface Props {
  detail: NonconformanceReport;
}

function StatusBadge({
  status,
  approvalInProgress,
}: {
  status?: string | null;
  approvalInProgress?: boolean | null;
}) {
  const key = approvalInProgress ? 'pending' : (status || 'OPEN').toLowerCase();
  return (
    <span className={`ncr-status is-${key}`}>
      {ncrStatusLabel(status, approvalInProgress)}
    </span>
  );
}

function NcrPreviewView({ detail }: Props) {
  return (
    <div className="ncr-detail">
      <div
        className="ifs-sheet"
        style={{ marginBottom: 14, padding: '12px 14px', background: '#fff' }}
      >
        <div className="ifs-sheet__top" style={{ marginBottom: 0 }}>
          <div>
            <h4 className="ifs-sheet__title" style={{ margin: 0, fontSize: 15 }}>
              {detail.itemName || '부적합품 보고서'}
            </h4>
            <p className="ifs-sheet__subtitle" style={{ margin: '4px 0 0' }}>
              {detail.reportNo || '-'} · {ncrStatusLabel(detail.status, detail.approvalInProgress)}
            </p>
          </div>
          <ApprovalStampBox
            authorName={detail.authorName || detail.reporterName}
            reviewerName={detail.reviewerName}
            approverName={detail.approverName}
          />
        </div>
      </div>
      <section className="ncr-detail__card">
        <h3>기본 정보</h3>
        <dl>
          <dt>보고서 번호</dt>
          <dd>{detail.reportNo || '-'}</dd>
          <dt>발생일</dt>
          <dd>{detail.reportDate || '-'}</dd>
          <dt>발생 단계</dt>
          <dd>{ncrSourceLabel(detail.sourceType)}</dd>
          <dt>연결 검사</dt>
          <dd>{detail.sourceInspectionId ? `#${detail.sourceInspectionId}` : '-'}</dd>
          <dt>상태</dt>
          <dd>
            <StatusBadge status={detail.status} approvalInProgress={detail.approvalInProgress} />
          </dd>
          <dt>작성자</dt>
          <dd>{detail.reporterName || detail.authorName || '-'}</dd>
        </dl>
      </section>
      <section className="ncr-detail__card">
        <h3>대상·조치</h3>
        <dl>
          <dt>품명</dt>
          <dd>{detail.itemName || '-'}</dd>
          <dt>규격</dt>
          <dd>{detail.specText || '-'}</dd>
          <dt>LOT</dt>
          <dd>{detail.lotNo || '-'}</dd>
          <dt>수량</dt>
          <dd>{detail.qty || '-'}</dd>
          <dt>조치 구분</dt>
          <dd>{ncrDispositionLabel(detail.disposition)}</dd>
        </dl>
      </section>
      <section className="ncr-detail__card is-wide">
        <h3>재고 차감 라인</h3>
        <p className="ncr-form__hint" style={{ marginTop: 0 }}>
          중간검사 투입과 별도로, 추가 폐기·반품분만 반영합니다.
          {ncrAffectsInventory(detail.disposition) && detail.status === 'CLOSED'
            ? ' (종결됨 · 재고 반영)'
            : ''}
        </p>
        {(detail.stockLines || []).length === 0 ? (
          <p className="ncr-form__hint">등록된 차감 라인이 없습니다.</p>
        ) : (
          <ul className="ncr-stock-detail">
            {(detail.stockLines || []).map((line, idx) => (
              <li key={line.lineId ?? `${line.inventoryId}-${idx}`}>
                <strong>{line.itemName || `재고 #${line.inventoryId}`}</strong>
                <span>
                  {[
                    line.specText,
                    line.lotNo ? `LOT ${line.lotNo}` : null,
                    `수량 ${line.qty}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="ncr-detail__card">
        <h3>QC 카테고리</h3>
        <dl>
          <dt>불량 유형</dt>
          <dd>{ncrDefectCategoryLabel(detail.defectCategory)}</dd>
          <dt>원인 분류</dt>
          <dd>{ncrCauseCategoryLabel(detail.causeCategory)}</dd>
        </dl>
      </section>
      <section className="ncr-detail__card is-wide">
        <h3>내용</h3>
        <dl>
          <dt>부적합 내용</dt>
          <dd>{detail.defectDesc || '-'}</dd>
          <dt>원인</dt>
          <dd>{detail.cause || '-'}</dd>
          <dt>시정·예방 조치</dt>
          <dd>{detail.correctiveAction || '-'}</dd>
          <dt>비고</dt>
          <dd>{detail.remark || '-'}</dd>
        </dl>
      </section>
    </div>
  );
}

export default NcrPreviewView;
